import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Navigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowRight, Moon, Sun } from '@phosphor-icons/react';

function formatApiError(detail) {
  if (!detail) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

export default function LoginPage() {
  const { user, login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen login-mesh flex" data-testid="login-page">
      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        data-testid="login-theme-toggle"
        className="absolute top-5 right-5 p-2 hover:bg-secondary rounded-lg transition-colors text-muted-foreground hover:text-foreground z-20"
      >
        {theme === 'dark' ? <Sun size={18} weight="bold" /> : <Moon size={18} weight="bold" />}
      </button>

      {/* Left — Brand/Editorial */}
      <div className="hidden lg:flex lg:w-[55%] items-end p-14 relative">
        <div className="animate-fade-in max-w-xl">
          <div className="flex items-center gap-2.5 mb-12">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#E04B2A' }}>
              <span className="text-sm font-extrabold text-white font-display">A</span>
            </div>
            <span className="font-display font-bold text-lg tracking-tight text-foreground">Admitrix</span>
          </div>
          <h1 className="font-display text-[clamp(2.5rem,5vw,4.5rem)] font-extrabold text-foreground leading-[1.05] tracking-[-0.02em]">
            The Admissions<br />
            <span style={{ color: '#E04B2A' }}>Operating System.</span>
          </h1>
          <p className="text-base text-muted-foreground mt-6 max-w-md leading-relaxed">
            Multi-branch lead management, admissions workflows, and fee collection — built for Indian education.
          </p>
          <div className="flex gap-6 mt-10">
            <Stat value="42" label="Active Leads" />
            <Stat value="5" label="Branches" />
            <Stat value="98%" label="Uptime" />
          </div>
        </div>
      </div>

      {/* Right — Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm animate-fade-in-delay-2">
          {/* Mobile brand */}
          <div className="lg:hidden flex items-center gap-2 mb-10">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#E04B2A' }}>
              <span className="text-xs font-extrabold text-white font-display">A</span>
            </div>
            <span className="font-display font-bold text-lg tracking-tight text-foreground">Admitrix</span>
          </div>

          <div className="mb-8">
            <h2 className="font-display text-2xl font-bold text-foreground tracking-tight">Sign in</h2>
            <p className="text-sm text-muted-foreground mt-1">Enter your credentials to continue</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@admitrix.com"
                data-testid="login-email-input"
                className="mt-1 rounded-lg h-11 text-sm border-border text-foreground placeholder:text-muted-foreground/40"
                required
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[11px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                data-testid="login-password-input"
                className="mt-1 rounded-lg h-11 text-sm border-border text-foreground placeholder:text-muted-foreground/40"
                required
              />
            </div>

            <div className="flex justify-end -mt-1">
              <Link to="/forgot-password" className="text-[11px] text-muted-foreground hover:text-foreground transition-colors mt-1 inline-block" data-testid="forgot-password-link">Forgot password?</Link>
            </div>

            {error && (
              <div data-testid="login-error" className="text-xs font-medium px-3 py-2.5 rounded-lg border" style={{ color: '#E04B2A', backgroundColor: 'rgba(224,75,42,0.06)', borderColor: 'rgba(224,75,42,0.15)' }}>
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 rounded-lg text-sm font-bold tracking-wide transition-all gap-2 text-white"
              style={{ backgroundColor: '#E04B2A' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#C93E22'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = '#E04B2A'}
            >
              {loading ? 'Signing in...' : 'Sign in'}
              {!loading && <ArrowRight size={15} weight="bold" />}
            </Button>
          </form>

          <div className="mt-8">
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-bold">Quick Login</span>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {[
                { label: 'Admin', email: 'admin@admitrix.com', password: 'Admin@123', color: '#E04B2A' },
                { label: 'Manager', email: 'ravi.sharma@admitrix.com', password: 'Password@123', color: '#10A882' },
                { label: 'Counselor', email: 'meera.joshi@admitrix.com', password: 'Password@123', color: '#2B6EF0' },
                { label: 'Front Desk', email: 'pooja.gupta@admitrix.com', password: 'Password@123', color: '#8B5CF6' },
                { label: 'Finance', email: 'suresh.menon@admitrix.com', password: 'Password@123', color: '#E5A100' },
                { label: 'Faculty', email: 'arun.iyer@admitrix.com', password: 'Password@123', color: '#06B6D4' },
              ].map((cred) => (
                <button
                  key={cred.label}
                  type="button"
                  onClick={() => { setEmail(cred.email); setPassword(cred.password); }}
                  data-testid={`demo-${cred.label.toLowerCase()}`}
                  className="h-9 rounded-lg border border-border text-[11px] font-bold text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors flex items-center justify-center gap-1.5"
                >
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: cred.color }} />
                  {cred.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <p className="font-display text-2xl font-extrabold text-foreground">{value}</p>
      <p className="text-[11px] text-muted-foreground mt-0.5 font-mono uppercase tracking-wider">{label}</p>
    </div>
  );
}
